import { useState } from "react";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import Modal from "../components/Modal";
import { getStripe, isStripeConfigured } from "../lib/stripe";
import { createSetupIntent } from "../api/paymentMethods";
import "./AddCardModal.scss";

/** CardElement 外观配置（Stripe 官方 API，非行内 style） */
const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: "15px",
      color: "#1f2328",
      fontFamily:
        "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      "::placeholder": { color: "#9aa1ad" },
    },
    invalid: { color: "#dc2626" },
  },
};

/** 表单本体：必须在 <Elements> 内才能用 useStripe / useElements */
const AddCardForm: React.FC<{
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return; // Stripe.js 尚未加载
    const card = elements.getElement(CardElement);
    if (!card) return;

    setError(null);
    setSubmitting(true);
    try {
      // 1. 取 clientSecret
      const { clientSecret } = await createSetupIntent();
      // 2. 确认绑卡（3DS 由 Stripe.js 自动处理）
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (result.error) {
        setError(result.error.message || "Failed to add card.");
      } else if (result.setupIntent?.status === "succeeded") {
        onSuccess();
      } else {
        setError("Card could not be saved. Please try again.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add card.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-add-card">
      <h2 className="cs-add-card__title">Add New Card</h2>
      <div className="cs-add-card__label">Card details</div>
      <div className="cs-add-card__element">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && <p className="cs-add-card__err">{error}</p>}

      <div className="cs-add-card__actions">
        <button
          type="button"
          className="cs-add-card__cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="cs-add-card__primary"
          disabled={!stripe || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Adding…" : "Add Card"}
        </button>
      </div>
    </div>
  );
};

/** 添加信用卡弹窗（流程一：绑卡） */
const AddCardModal: React.FC<{
  open: boolean;
  onClose: () => void;
  /** 绑卡成功回调（用于刷新列表） */
  onAdded: () => void;
}> = ({ open, onClose, onAdded }) => {
  return (
    <Modal open={open} onClose={onClose}>
      {isStripeConfigured ? (
        <Elements stripe={getStripe()}>
          <AddCardForm
            onSuccess={() => {
              onAdded();
              onClose();
            }}
            onCancel={onClose}
          />
        </Elements>
      ) : (
        <div className="cs-add-card">
          <h2 className="cs-add-card__title">Add New Card</h2>
          <p className="cs-add-card__notice">
            Payment is not configured. Set
            <code> REACT_APP_STRIPE_PUBLISHABLE_KEY </code>
            to enable adding cards.
          </p>
          <div className="cs-add-card__actions">
            <button
              type="button"
              className="cs-add-card__cancel"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddCardModal;
